import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import CommonButton from "../../commonButton";
import { fetchActiveCities } from "../../../redux/slice/locationSlice";
import { fetchCompanyCategories } from "../../../redux/slice/companySlice";
import withModal from "../../withModal";
import { useToast } from "../../../context/TostContext";
import InputField from "../../form/InputField";
import CustomSelect from "../../form/CustomSelect";

const EditCompanyProfileModal = ({ onClose, onSubmit, company, isLoading }) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    name: "",
    tagline: "",
    email: "",
    mobile_number: "",
    whatsapp_number: "",
    website: "",
    address: "",
    about: "",
    category_id: "",
    city_id: "",
    status: "active",
  });
  const toast = useToast();
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);

  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const coverInputRef = useRef(null);

  const fetchCategoryOptions = useCallback(async (search, page) => {
    try {
      const result = await dispatch(fetchCompanyCategories({ search, page, limit: 20 })).unwrap();
      return { data: result.data || [], meta: result.meta || { page: 1, pages: 1 } };
    } catch (err) {
      console.error("Error fetching company categories:", err);
      return { data: [], meta: { page: 1, pages: 1 } };
    }
  }, [dispatch]);

  const fetchCityOptions = useCallback(async (search, page) => {
    try {
      const result = await dispatch(fetchActiveCities({ search, page, limit: 20 })).unwrap();
      return { data: result.data || [], meta: result.meta || { page: 1, pages: 1 } };
    } catch (err) {
      console.error("Error fetching du cities:", err);
      return { data: [], meta: { page: 1, pages: 1 } };
    }
  }, [dispatch]);


  useEffect(() => {
    if (company) {
      setFormData({
        name: company?.name || "",
        tagline: company?.tagline || "",
        email: company?.profile?.email || "",
        mobile_number: company?.profile?.mobile_number || "",
        whatsapp_number: company?.profile?.whatsapp_number || "",
        website: company?.profile?.website || "",
        address: company?.profile?.address || "",
        about: company?.profile?.about || "",
        category_id: company?.category_id || "",
        city_id: company?.profile?.city_id || "",
        status: company?.status || "active",
      });
      setLogoFile(null);
      setLogoPreview(company?.profile?.logo?.url || null);
      setCoverFile(null);
      setCoverPreview(company?.profile?.cover_photo?.url || null);
    } else {
      setFormData({
        name: "",
        tagline: "",
        email: "",
        mobile_number: "",
        whatsapp_number: "",
        website: "",
        address: "",
        about: "",
        category_id: "",
        city_id: "",
        status: "active",
      });
      setLogoFile(null);
      setLogoPreview(null);
      setCoverFile(null);
      setCoverPreview(null);
    }
  }, [company]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDropdownChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogoChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast.error("Logo size should be less than 5MB");
        return;
      }
      setLogoFile(selectedFile);
      setLogoPreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleCoverChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast.error("Cover photo size should be less than 5MB");
        return;
      }
      setCoverFile(selectedFile);
      setCoverPreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isLoading) return;

    if (!formData.name.trim()) {
      toast.error("Please enter a company name");
      return;
    }

    if (!formData.category_id) {
      toast.error("Please select a category");
      return;
    }

    const submitData = new FormData();

    submitData.append("name", formData.name);
    submitData.append("tagline", formData.tagline || "");
    submitData.append("mobile_number", formData.mobile_number || "");
    submitData.append("whatsapp_number", formData.whatsapp_number || "");
    submitData.append("email", formData.email || "");
    submitData.append("about", formData.about || "");
    submitData.append("category_id", formData.category_id);
    submitData.append("city_id", formData.city_id || "");
    submitData.append("status", formData.status || "active");

    let websiteUrl = (formData.website || "").trim();
    if (websiteUrl && !/^https?:\/\//i.test(websiteUrl)) {
      websiteUrl = 'https://' + websiteUrl;
    }
    submitData.append("website", websiteUrl);
    submitData.append("address", formData.address || "");

    if (logoFile) {
      submitData.append("logo", logoFile);
    }
    if (coverFile) {
      submitData.append("cover_photo", coverFile);
    }

    onSubmit(submitData);
  };

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <Modal.Body>
        {/* Images Section */}
        <div className="mb-5 position-relative">
          {/* Cover Photo */}
          <div
            className="position-relative overflow-hidden cursor-pointer w-100 rounded-3"
            style={{
              height: "180px",
              backgroundColor: "#f8f9fa",
              borderBottom: coverPreview ? "none" : "1px solid #dee2e6",
              transition: "all 0.2s"
            }}
            onClick={() => coverInputRef.current?.click()}
            title="Change Cover Photo"
          >
            {coverPreview ? (
              <>
                <img src={coverPreview} alt="Cover Preview" className="w-100 h-100 object-fit-cover" />
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center hover-overlay" style={{ backgroundColor: "rgba(0,0,0,0.3)", opacity: 0, transition: "opacity 0.2s" }} onMouseEnter={(e) => e.currentTarget.style.opacity = 1} onMouseLeave={(e) => e.currentTarget.style.opacity = 0}>
                  <div className="bg-white rounded-circle p-2 shadow-sm d-flex align-items-center justify-content-center">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
                  </div>
                </div>
              </>
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
                <span className="small fw-medium">Upload Cover Photo</span>
              </div>
            )}
          </div>
          <input type="file" ref={coverInputRef} onChange={handleCoverChange} accept="image/*" className="d-none" />

          {/* Logo Photo */}
          <div
            className="position-absolute cursor-pointer shadow-sm"
            style={{
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              border: "4px solid #fff",
              backgroundColor: "#fff",
              bottom: "-40px",
              left: "40px",
              zIndex: 10
            }}
            onClick={() => logoInputRef.current?.click()}
            title="Change Company Logo"
          >
            <div className="w-100 h-100 rounded-circle overflow-hidden bg-light d-flex align-items-center justify-content-center position-relative">
              {logoPreview ? (
                <>
                  <img src={logoPreview} alt="Logo Preview" className="w-100 h-100 object-fit-cover" />
                  <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center hover-overlay" style={{ backgroundColor: "rgba(0,0,0,0.4)", opacity: 0, transition: "opacity 0.2s" }} onMouseEnter={(e) => e.currentTarget.style.opacity = 1} onMouseLeave={(e) => e.currentTarget.style.opacity = 0}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
                  </div>
                </>
              ) : (
                <div className="d-flex flex-column align-items-center text-muted">
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-1"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                  <span className="small fw-medium">Logo</span>
                </div>
              )}
            </div>
            <div
              className="position-absolute rounded-circle bg-primary shadow-sm d-flex align-items-center justify-content-center text-white"
              style={{ width: "32px", height: "32px", bottom: "2px", right: "2px", border: "2px solid #fff" }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
            </div>
          </div>
          <input type="file" ref={logoInputRef} onChange={handleLogoChange} accept="image/*" className="d-none" />
        </div>

        <div className="grid gap-3 px-2 my-4">
          <InputField
            className="g-col-md-6"
            label={{ name: "Company Name" }}
            required
            input={{
              type: "text",
              name: "name",
              value: formData.name,
              onChange: handleChange,
              placeholder: "Enter company name",
            }}
          />

          <InputField
            className="g-col-md-6"
            label={{ name: "Tagline" }}
            input={{
              type: "text",
              name: "tagline",
              value: formData.tagline,
              onChange: handleChange,
              placeholder: "Enter company tagline",
            }}
          />

          <InputField
            className="g-col-md-4"
            label={{ name: "Email" }}
            input={{
              type: "email",
              name: "email",
              value: formData.email,
              onChange: handleChange,
              placeholder: "Ex: info@r-point.com",
            }}
          />

          <InputField
            className="g-col-md-4"
            label={{ name: "Mobile Number" }}
            input={{
              type: "text",
              name: "mobile_number",
              value: formData.mobile_number,
              onChange: handleChange,
              placeholder: "Enter 10-digit mobile number",
            }}
          />

          <InputField
            className="g-col-md-4"
            label={{ name: "WhatsApp Number" }}
            input={{
              type: "text",
              name: "whatsapp_number",
              value: formData.whatsapp_number,
              onChange: handleChange,
              placeholder: "Enter WhatsApp number",
            }}
          />


          <CustomSelect
            className="g-col-12 g-col-md-4"
            label={{ name: "Category" }}
            name="category_id"
            placeholder="Select Category"
            value={formData.category_id}
            initialLabel={company?.category?.name}
            onChange={handleDropdownChange}
            fetchOptions={fetchCategoryOptions} />

          <CustomSelect
            isClearable
            className="g-col-12 g-col-md-4"
            label={{ name: "City" }}
            name="city_id"
            placeholder="Select City"
            value={formData.city_id}
            initialLabel={company?.profile?.city?.name}
            onChange={handleDropdownChange}
            fetchOptions={fetchCityOptions}
          />

          <InputField
            className="g-col-md-12"
            label={{ name: "Website" }}
            input={{
              type: "text",
              name: "website",
              value: formData.website,
              onChange: handleChange,
              placeholder: "Ex: https://r-point.com",
            }}
          />

          <InputField
            className="g-col-md-12"
            label={{ name: "Address" }}
            input={{
              type: "text",
              name: "address",
              value: formData.address,
              onChange: handleChange,
              placeholder: "Enter full address",
            }}
          />

          <InputField
            className="g-col-md-12"
            label={{ name: "About" }}
            input={{
              as: "textarea",
              rows: 4,
              name: "about",
              value: formData.about,
              onChange: handleChange,
              placeholder: "Write something about the company",
            }}
          />
        </div>
      </Modal.Body>
      <Modal.Footer className="border-top-0 pt-0">
        <CommonButton
          type="button"
          variant="outline"
          title="Cancel"
          onClick={onClose}
          disabled={isLoading}
        />
        <CommonButton
          type="submit"
          variant="primary"
          title="Save Changes"
          isLoading={isLoading}
        />
      </Modal.Footer>
    </Form>
  );
};

export default withModal(EditCompanyProfileModal);
